import { ref, computed } from 'vue'
import { storeToRefs, defineStore, acceptHMRUpdate } from 'pinia'
import { useClientStore } from '@shared/stores/client.js'
import { useModals } from '@/composables/modals.js'
import { useSubscriptionStore } from '@/stores/subscribe.js'
import useAnalytics from '@shared/composables/analytics.js'
import { useRouter } from 'vue-router'
import { usePlans } from '@shared/composables/plans.js'
import { useExperimentsStore } from '@shared/stores/experiments.js'

export const useRejoinStore = defineStore('rejoin', () => {
  const { formerMember, latestSubscriptionPlanId } = storeToRefs(useClientStore())
  const { membershipPlans } = storeToRefs(useSubscriptionStore())

  const bannerDismissed = ref(false)
  const modalDismissed = ref(false)
  const isRejoinFlow = ref(false)
  const marketingConsentRejoin = ref(true)

  const showRejoinBanner = computed(() => !bannerDismissed.value && formerMember.value)
  const showRejoinModal = computed(() => !modalDismissed.value && formerMember.value)
  const latestPlan = computed(() => membershipPlans.value?.find(plan => plan.id === latestSubscriptionPlanId.value))

  const { selectMembershipPlan } = useSubscriptionStore()
  const { initializeMembershipPlans } = usePlans()
  const { openRejoinModal, closeRejoinModal } = useModals()
  const { trackOverlayInteraction } = useAnalytics()
  const router = useRouter()

  async function initializeRejoinData () {
    if (formerMember.value) {
      await initializeMembershipPlans()
    }
    if (showRejoinModal.value) {
      openRejoinModal()
    }
  }

  function dismissRejoinBanner () {
    bannerDismissed.value = true
    trackOverlayInteraction('rejoin', 'banner', 'dismiss')
  }

  function dismissRejoinModal () {
    modalDismissed.value = true
    closeRejoinModal()
    trackOverlayInteraction('rejoin', 'modal', 'dismiss')
  }

  async function beginRejoinFlow (planId = latestSubscriptionPlanId.value) {
    isRejoinFlow.value = true
    modalDismissed.value = true
    await selectMembershipPlan({ planId })
    const to = {
      name: 'subscribe',
      query: {
        rejoin: true
      }
    }
    await useExperimentsStore().fetchRegisteredExperiments(router.resolve(to).fullPath)
    router.push(to)
  }

  return {
    latestSubscriptionPlanId,
    formerMember,
    initializeRejoinData,
    showRejoinBanner,
    dismissRejoinBanner,
    showRejoinModal,
    dismissRejoinModal,
    isRejoinFlow,
    beginRejoinFlow,
    marketingConsentRejoin,
    latestPlan
  }
})

import.meta.hot?.accept(acceptHMRUpdate(useRejoinStore, import.meta.hot))
